import React from 'react';
import { Menu, MenuItem, MenuButton, MenuDivider } from '@szhsin/react-menu';
import '@szhsin/react-menu/dist/index.css';
import '@szhsin/react-menu/dist/transitions/slide.css';
import {
  EllipsisVertical, Eye, Pencil, Trash2, Check, Ban, Download,
  Send, RotateCcw, Pause, Play, UserCheck, Archive, Lock, Megaphone, XCircle,
  ArrowRightLeft, ThumbsUp, ThumbsDown, FileSignature, UserMinus, UserX,
  ExternalLink, Copy, CircleCheck, CalendarClock, CalendarX, ClipboardList,
  ShieldAlert, Upload, ClipboardCheck, CircleSlash,
  Users, SquareKanban, Workflow, FilePlus2, FilePen, TimerOff,
  CheckCheck, BadgeCheck, Circle, RefreshCw, MessagesSquare, KeyRound, LockOpen,
} from 'lucide-react';
import styled from 'styled-components';

// ---------------------------------------------------------------------------
// NEDEN SESSİZ YEDEK (fallback) YOK
//
// Bu dosya eskiden `iconMap[action.type] || { icon: Eye, ... }` diyordu.
// Haritada karşılığı OLMAYAN her tip sessizce AYNI göz ikonuna düşüyordu; bir
// menüde iki-üç aksiyon varsa kullanıcı yan yana ÜÇ AYNI ikon görüyordu ve
// hangisinin ne yaptığını ikondan ayırt edemiyordu.
//
// Bu tuzak ÜÇ KEZ yaşandı:
//   1) Belge yaşam döngüsü — `upload` / `decide` / `waive` haritada yoktu,
//      üçü de kalem/göz ikonuyla çizildi (müşteri bildirimi).
//   2) ATS — `view_applications` / `pipeline_board` / `pipeline_stages` /
//      `apply` haritada yoktu (üç göz yan yana); teklif ekranında `iconType`
//      beyan etmeyen geçişler `edit`'e düşüp üç kalem yan yana çıktı.
//   3) Yan hak / yaşam olayları — `acknowledge` / `resolve` / `expire` haritada
//      yoktu: üç aksiyon, tek göz ikonu.
//
// DÖRDÜNCÜSÜ OLMASIN diye yedek KALDIRILDI: eşleşmeyen tip artık göz ikonuna
// DÜŞMEZ, nötr bir daire çizer ve geliştirme ortamında konsola uyarı basar.
// Böylece eksik eşleme üretime çıkmadan, ilk render'da görülür.
// ---------------------------------------------------------------------------

const StyledMenuButton = styled(MenuButton)`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: none;
  border-radius: 6px;
  background: none;
  cursor: pointer;
  color: ${({ theme }) => theme.colors.text.dark.little};
  transition: all 0.15s;

  &:hover {
    background: ${({ theme }) => theme.colors.bgColor};
    color: ${({ theme }) => theme.colors.text.primary};
  }
`;

const ItemContent = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 13px;
`;

const iconMap = {
  details: { icon: Eye, color: '#0291EF' },
  edit: { icon: Pencil, color: '#F59E0B' },
  delete: { icon: Trash2, color: '#E53935' },
  approve: { icon: Check, color: '#10B981' },
  reject: { icon: ThumbsDown, color: '#E53935' },
  download: { icon: Download, color: '#6B7280' },
  open: { icon: ExternalLink, color: '#0291EF' },
  copy: { icon: Copy, color: '#6B7280' },
  // Donmuş bir belgeyi bilerek tazeleme (izin mutabakat formu — düzeltme
  // turu 2, bulgu G). İndirmeden (download) AYRI bir ikon taşır: indirme
  // donmuş dökümü basar, bu onu YENİLER.
  regenerate: { icon: RefreshCw, color: '#0291EF' },
  // Çalışanın iki adımlı doğrulamasını sıfırlama (telefonunu kaybeden kullanıcı).
  reset_mfa: { icon: KeyRound, color: '#F59E0B' },
  // Çalışanın giriş kilidini açma (hatalı parola denemeleriyle kilitlenen hesap) — AKM POC §9.2.
  unlock_login: { icon: LockOpen, color: '#0291EF' },

  // Status transitions — ATS requisition / job posting state machine
  submit_for_approval: { icon: Send, color: '#0291EF' },
  cancel: { icon: XCircle, color: '#E53935' },
  return_to_draft: { icon: RotateCcw, color: '#F59E0B' },
  hold: { icon: Pause, color: '#F59E0B' },
  resume: { icon: Play, color: '#10B981' },
  fill: { icon: UserCheck, color: '#10B981' },
  publish: { icon: Send, color: '#0291EF' },
  pause: { icon: Pause, color: '#F59E0B' },
  close: { icon: Lock, color: '#6B7280' },
  archive: { icon: Archive, color: '#6B7280' },
  convert_to_posting: { icon: Megaphone, color: '#0291EF' },

  // Application-specific transitions
  move_stage: { icon: ArrowRightLeft, color: '#0291EF' },
  offer: { icon: FileSignature, color: '#10B981' },
  accept: { icon: ThumbsUp, color: '#10B981' },
  withdraw: { icon: UserMinus, color: '#6B7280' },
  disqualify: { icon: UserX, color: '#E53935' },

  // ATS interview actions
  scorecard: { icon: ClipboardList, color: '#7C3AED' },
  complete: { icon: CircleCheck, color: '#10B981' },
  reschedule: { icon: CalendarClock, color: '#F59E0B' },
  no_show: { icon: CalendarX, color: '#EA580C' },
  // Panel debrief (SPEC F4) — karşılaştırma + karar sayfası. Haritada olmasaydı
  // nötr daireye düşerdi (GN-4: eksik eşleme sessiz kalmasın).
  debrief: { icon: MessagesSquare, color: '#0EA5A4' },

  // ATS offer — salary-band exception approval (gates the offer Approve)
  band_exception: { icon: ShieldAlert, color: '#F59E0B' },
  // ATS offer — re-send the candidate e-mail with a fresh acceptance link
  resend: { icon: Send, color: '#0291EF' },

  // Belge yaşam döngüsü. Bu üçü daha önce YOKTU; çağıran ekranlar hepsine `edit`
  // veriyordu, dolayısıyla "Yükle", "Karar Ver" ve "Feragat Et" aynı kalem ikonunu
  // gösteriyordu (müşteri bildirimi). Ayrıca `key={action.type || i}` olduğu için
  // aynı tip birden çok kez kullanıldığında React MÜKERRER KEY alıyordu.
  upload: { icon: Upload, color: '#0291EF' },
  decide: { icon: ClipboardCheck, color: '#7C3AED' },
  waive: { icon: CircleSlash, color: '#6B7280' },

  // ATS — ilan/aday satır aksiyonları. Üçü de haritada YOKTU: "Başvuruları
  // Gör", "Aday Havuzu Panosu" ve "Süreç Aşamaları" aynı göz ikonuyla
  // çiziliyordu.
  view_applications: { icon: Users, color: '#0291EF' },
  pipeline_board: { icon: SquareKanban, color: '#7C3AED' },
  pipeline_stages: { icon: Workflow, color: '#0EA5A4' },
  apply: { icon: FilePlus2, color: '#10B981' },

  // ATS teklif durum makinesi. `iconType` beyan etmeyen geçişler `edit`'e
  // düşüyordu → taslak/onayda/gönderildi durumlarında üç kalem yan yana.
  revise: { icon: FilePen, color: '#F59E0B' },
  send: { icon: Send, color: '#0291EF' },
  expire: { icon: TimerOff, color: '#EA580C' },

  // Eş anlamlı tipler. Ekranlar `details`/`detail` ve `delete`/`remove`
  // ikilisini karışık kullanıyor; ikisi de eşlenmeli, yoksa biri yedeğe düşer.
  detail: { icon: Eye, color: '#0291EF' },
  view: { icon: Eye, color: '#0291EF' },
  remove: { icon: Trash2, color: '#E53935' },
  submit: { icon: Send, color: '#0291EF' },

  // Yaşam olayları (BN) — üçü de haritada yoktu, üç göz ikonu çiziliyordu.
  acknowledge: { icon: CheckCheck, color: '#0291EF' },
  resolve: { icon: BadgeCheck, color: '#10B981' },
};

// Eşleşmeyen tipte kullanılan NÖTR ikon. Bilerek "anlamsız" bir şekil: bir
// aksiyonun gerçekten ne yaptığını ima etmez, sadece "bu tipin ikonu
// tanımlanmamış" der.
const UNMAPPED_ICON = { icon: Circle, color: '#9CA3AF' };
const warnedTypes = new Set();

function resolveIcon(type) {
  const config = iconMap[type];
  if (config) return config;
  if (process.env.NODE_ENV !== 'production' && !warnedTypes.has(type)) {
    warnedTypes.add(type);
    // eslint-disable-next-line no-console
    console.warn(
      `[OptionsMenu] "${type}" tipi iconMap'te tanımlı değil. `
      + 'Nötr ikon çizildi. Lütfen components/ui/OptionsMenu.js içindeki iconMap\'e '
      + 'bu tip için ayırt edilebilir bir ikon ekleyin (veya aksiyona `icon` verin).',
    );
  }
  return UNMAPPED_ICON;
}

export default function OptionsMenu({ actions = [], row }) {
  if (actions.length === 0) return null;

  return (
    <Menu
      menuButton={
        <StyledMenuButton onClick={(e) => e.stopPropagation()}>
          <EllipsisVertical size={16} />
        </StyledMenuButton>
      }
      transition
      align="end"
      portal
      menuStyle={{ minWidth: 160, borderRadius: 8, boxShadow: '0 8px 24px rgba(0,66,66,0.1)', zIndex: 2000 }}
    >
      {actions.map((action, i) => {
        if (action.type === 'divider') return <MenuDivider key={`div-${i}`} />;
        if (action.condition && !action.condition(row)) return null;

        // Aksiyon kendi `icon`'unu verdiyse eksik eşleme diye uyarılmaz —
        // bilinçli, ekrana özel bir ikondur.
        const iconConfig = iconMap[action.type]
          || (action.icon ? { icon: action.icon, color: '#6B7280' } : resolveIcon(action.type));
        const Icon = action.icon || iconConfig.icon;
        const color = action.color || iconConfig.color;

        return (
          <MenuItem
            key={action.type || i}
            onClick={(e) => {
              e.syntheticEvent?.stopPropagation?.();
              e.stopPropagation?.();
              action.onClick?.(row);
            }}
            style={{ padding: '8px 14px' }}
          >
            <ItemContent>
              <Icon size={15} color={color} />
              {/* Etiket satıra göre değişebilir — `condition(row)` ile aynı sözleşme.
                  Çalışanlar listesinde tek aksiyon, çalışanın devam eden bir ayrılış
                  süreci olup olmamasına göre "Süreci Başlat" / "Süreci Aç" der. */}
              <span>{typeof action.label === 'function' ? action.label(row) : action.label}</span>
            </ItemContent>
          </MenuItem>
        );
      })}
    </Menu>
  );
}
